import { useState } from "react";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { X, Plus } from "lucide-react";

interface KeywordsInputProps {
  keywords: string[];
  error?: string | null;
  disabled?: boolean;
  onKeywordsChange: (keywords: string[]) => void;
}

export function KeywordsInput({
  keywords,
  error,
  disabled,
  onKeywordsChange,
}: KeywordsInputProps) {
  const [newKeyword, setNewKeyword] = useState("");

  const handleAdd = () => {
    const trimmed = newKeyword.trim();
    if (trimmed && !keywords.includes(trimmed)) {
      onKeywordsChange([...keywords, trimmed]);
      setNewKeyword("");
    }
  };

  const handleRemove = (keyword: string) => {
    onKeywordsChange(keywords.filter((k) => k !== keyword));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-ink-800">
        Keywords <span className="text-red-500">*</span>
      </label>
      <div className="space-y-3">
        {keywords.length > 0 && (
          <div>
            <p className="text-xs text-ink-500 mb-2">Keywords:</p>
            <div className="flex flex-wrap gap-2">
              {keywords.map((keyword, index) => (
                <Badge
                  key={index}
                  variant="outline"
                  className="text-xs flex items-center gap-1"
                >
                  {keyword}
                  <button
                    type="button"
                    onClick={() => handleRemove(keyword)}
                    className="ml-1 hover:text-red-600"
                    disabled={disabled}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        )}
        <div>
          <p className="text-xs text-ink-500 mb-2">Add keywords:</p>
          <div className="flex gap-2">
            <Input
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Enter keyword and press Enter or click +"
              className="flex-1"
              disabled={disabled}
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleAdd}
              disabled={!newKeyword.trim() || disabled}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

